import { useState, useEffect } from 'react';
import { useAuthenticatedApi } from './useAuthenticatedApi';
import { apiEndpoints, ApiError } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

interface SubscriptionAccessStatus {
  hasAccess: boolean;
  accessType: 'subscription' | 'credits' | 'free_access' | 'none' | 'error';
  loading: boolean;
  error: string | null;
  details?: any;
}

export const useSubscriptionAccess = () => {
  const api = useAuthenticatedApi();
  const { checkAuth } = useAuth();
  const [status, setStatus] = useState<SubscriptionAccessStatus>({
    hasAccess: false,
    accessType: 'none',
    loading: true,
    error: null,
    details: {}
  });

  useEffect(() => {
    const checkAccess = async () => {
      if (!api) {
        setStatus(prev => ({ ...prev, loading: false }));
        return;
      }

      try {
        const response = await api.get(apiEndpoints.billing.hasSubscriptionAccess);
        
        setStatus({
          hasAccess: response.has_access || false,
          accessType: response.access_type || 'none',
          loading: false,
          error: null,
          details: response.details || {}
        });
      } catch (error) {
        console.error('Failed to check subscription access:', error);
        // If unauthorized, surface an auth error so the guard can redirect to login instead of subscription page
        if (error instanceof ApiError && error.status === 401) {
          // Attempt to refresh local auth state; do not navigate here (handled by guard)
          checkAuth().catch(() => {});
          setStatus({
            hasAccess: false,
            accessType: 'error',
            loading: false,
            error: 'auth',
            details: {}
          });
          return;
        }
        setStatus({
          hasAccess: false,
          accessType: 'error',
          loading: false,
          error: 'Failed to verify access status',
          details: {}
        });
      }
    };

    checkAccess();
  }, [api, checkAuth]);

  return status;
}; 